import React from "react";
import { Box, Text, Inline, IconDate, IconTime, IconArrow, Hidden, IconHome, Stack, IconCompany } from "braid-design-system";
import LogoutButton from "./LogoutButton";
import { useLanguage } from "../contexts/LanguageProvider";

import { useFinancialDate } from "../contexts/FinancialDateProvider";
import { useWebSocket } from "../contexts/WebSocketProvider";
import { useLocalDateTime } from "../contexts/LocalDateTimeProvider";
import { usePageHeading } from "../contexts/PageHeadingProvider";
import { useAuth } from "../contexts/AuthProvider";
import logo from '../assets/logo.jpeg'; // Adjust the path and filename as needed
import { getSystemHealth, SystemHealthResponse } from "../services/systemHealthService";

const miniBadgeStyle = (isHealthy: boolean | null | undefined) => {
    if (isHealthy === null || isHealthy === undefined) {
        return {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '9.5px',
            fontWeight: 600,
            color: '#b45309',
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            padding: '1px 5px',
            borderRadius: '10px',
            whiteSpace: 'nowrap' as const
        };
    }
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '9.5px',
        fontWeight: 600,
        color: isHealthy ? '#15803d' : '#b91c1c',
        backgroundColor: isHealthy ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${isHealthy ? '#bbf7d0' : '#fecaca'}`,
        padding: '1px 5px',
        borderRadius: '10px',
        whiteSpace: 'nowrap' as const
    };
};

const wsBadgeStyle = (status: string) => {
    const isConnected = status === 'connected';
    const isConnecting = status === 'connecting';
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '9.5px',
        fontWeight: 600,
        color: isConnected ? '#15803d' : isConnecting ? '#b45309' : '#b91c1c',
        backgroundColor: isConnected ? '#f0fdf4' : isConnecting ? '#fffbeb' : '#fef2f2',
        border: `1px solid ${isConnected ? '#bbf7d0' : isConnecting ? '#fde68a' : '#fecaca'}`,
        padding: '1px 5px',
        borderRadius: '10px',
        whiteSpace: 'nowrap' as const
    };
};

const TopBar: React.FC = () => {
    const { currentFinancialDate, isDateMismatch } = useFinancialDate();
    const businessDateStr = currentFinancialDate ? currentFinancialDate.business_date.split("T")[0] : null;
    const serverDateStr = currentFinancialDate ? currentFinancialDate.server_date.split("T")[0] : new Date().toISOString().split('T')[0];
    const isBackDate = businessDateStr ? businessDateStr < serverDateStr : false;
    const isFutureDate = businessDateStr ? businessDateStr > serverDateStr : false;
    const { status: wsStatus } = useWebSocket();
    const { dateTime } = useLocalDateTime();
    const { pageHeading } = usePageHeading();
    const { user } = useAuth();
    const { available, lang, changeLanguage } = useLanguage();

    const [health, setHealth] = React.useState<SystemHealthResponse | null>(null);

    const fetchHealth = React.useCallback(() => {
        getSystemHealth().then((res) => {
            setHealth(res);
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error("TopBar failed to fetch health status", err);
        });
    }, []);

    React.useEffect(() => {
        fetchHealth();
        const interval = setInterval(fetchHealth, 10 * 60 * 1000); // 10 minutes
        return () => clearInterval(interval);
    }, [fetchHealth]);

    return (
        <Box
            paddingY={['small', 'small', 'small', 'small']}
            paddingX={['small', 'medium', 'large', 'large']}
            className="premium-topbar"
        >
            <Inline space={['small', 'small', 'large', 'large']} alignY="center">
                <Inline space="small" alignY="center">
                    <img src={logo} alt="Logo" height={30} />
                    <Stack space="xxsmall">
                        <Text weight="strong" size="small" icon={<IconCompany />}>
                            {user?.institute?.name_ln1}
                        </Text>
                        {user?.active_branch?.name_ln1 ? (
                            <Text size="xsmall" tone="secondary" icon={<IconHome />}>
                                {user?.active_branch?.name_ln1}
                            </Text>
                        ) : null}
                    </Stack>
                    <Hidden below="tablet">
                        <Inline space="small" alignY="center">
                            {pageHeading ? (
                                <Text size="small" icon={<IconArrow direction="right" />}>
                                    {pageHeading}
                                </Text>
                            ) : null}
                        </Inline>
                    </Hidden>
                </Inline>
                <Box flexGrow={1} />
                <Hidden below="tablet">
                    <Inline space={"small"} alignY="center">
                        {isBackDate && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: '#dc2626', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', padding: '4px 12px', borderRadius: '12px' }}>
                                ⚠️ Risk! Working in backdate.
                            </span>
                        )}
                        {isFutureDate && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: '#ea580c', backgroundColor: '#ffedd5', border: '1px solid #fed7aa', padding: '4px 12px', borderRadius: '12px' }}>
                                ⚠️ Risk! Working in future date.
                            </span>
                        )}
                        <Stack space="xxsmall">
                            <Text tone={isDateMismatch ? "critical" : "secondary"} size="xsmall" icon={<IconDate />}>
                                {currentFinancialDate ? new Date(currentFinancialDate.business_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Loading date...'}
                            </Text>
                            <Text tone="secondary" size="xsmall" icon={<IconTime />}>
                                {dateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                            </Text>
                        </Stack>
                        <Text>|</Text>

                        {/* WebSocket & Services Status Badges */}
                        <Stack space="xxsmall">
                            {/* WebSocket Status */}
                            <span style={wsBadgeStyle(wsStatus)}>
                                <span className={`status-dot ${wsStatus === 'connected' ? 'connected' : wsStatus === 'connecting' ? 'connecting' : 'disconnected'}`}></span>
                                Web Socket
                            </span>

                            {/* Queue Service Status */}
                            <span style={miniBadgeStyle(health ? health.cache_service : null)}>
                                <span className={`status-dot ${health ? (health.cache_service ? 'connected' : 'disconnected') : 'connecting'}`}></span>
                                Queue Service
                            </span>
                        </Stack>

                        <style>
                            {`
                            .status-dot {
                                width: 5px;
                                height: 5px;
                                border-radius: 50%;
                                display: inline-block;
                            }
                            .status-dot.connected {
                                background-color: #22c55e;
                                animation: pulse-green 2s infinite;
                            }
                            .status-dot.connecting {
                                background-color: #d97706;
                                animation: pulse-amber 1.5s infinite;
                            }
                            .status-dot.disconnected {
                                background-color: #ef4444;
                                animation: pulse-red 1.5s infinite;
                            }

                            @keyframes pulse-green {
                                0% {
                                    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                                }
                                70% {
                                    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0);
                                }
                                100% {
                                    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
                                }
                            }
                            @keyframes pulse-amber {
                                0% {
                                    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.7);
                                }
                                70% {
                                    box-shadow: 0 0 0 4px rgba(217, 119, 6, 0);
                                }
                                100% {
                                    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0);
                                }
                            }
                            @keyframes pulse-red {
                                0% {
                                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
                                }
                                70% {
                                    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
                                }
                                100% {
                                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
                                }
                            }
                            `}
                        </style>

                        {/* Language selector */}
                        <select
                            aria-label="Language"
                            value={lang}
                            onChange={(e) => changeLanguage(e.target.value)}
                            style={{
                                marginLeft: 8,
                                padding: '4px 8px',
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                fontSize: '12px',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer'
                            }}
                        >
                            {available.map((l) => (
                                <option key={l.code} value={l.code}>{l.name || l.code}</option>
                            ))}
                        </select>
                    </Inline>
                </Hidden>
                <LogoutButton />
            </Inline>
        </Box>
    );
};

export default TopBar;
